/**
 * useDiskTemplate.ts
 */
import Focus from '@shared/types/enums/focus';
import TranslationManager from '@shared/assets/locale/translationsManager';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { popRoute } from '@shared/store/slices/router.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';

/**
 * Constants
 */
const LITERALS = 'PAGES.SETTINGS.DISK';

/**
 * Props interface for the Disk template component.
 */
export interface Props {
  days: string;
  quota: number;
  used: number;
}

/**
 * Hook to use the disk template.
 * Handles the disk template logic.
 */
export default function useDiskTemplate(parentProps: Props) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.DISK));
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.DISK));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Disk keyboard
  useKeyboard({
    focus: Focus.DISK,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.DISK));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const { days, quota, used } = parentProps;
    return {
      callbacks: {
        mainContainer: {
          onMouseEnter,
        },
      },
      literals: {
        title: TranslationManager.get(`${LITERALS}.TITLE`),
        info: TranslationManager.get(`${LITERALS}.INFO`),
        detail: TranslationManager.get(`${LITERALS}.DETAIL`, days),
        barInfo: TranslationManager.get(
          `${LITERALS}.BAR_INFO`,
          `${used || '  '}`
        ),
        maxCapacity: TranslationManager.get(
          `${LITERALS}.MAX_CAPACITY`,
          `${quota || '  '}`
        ),
      },
      props: {
        diskCapacityInnerBar: {
          width: (used && quota && used / quota) || 0,
        },
      },
    };
  }, [parentProps]);
}
